import { apiClient } from './apiClient';
import type { ChatbotPersonaListItem, SendVoiceResponse, VoiceQuotaStatus } from '../types/messaging';

export const chatbotService = {
    /**
     * List available AI chatbot personas.
     */
    async listPersonas(): Promise<ChatbotPersonaListItem[]> {
        const response = await apiClient.get<{ items: ChatbotPersonaListItem[] }>(
            '/messaging/ai/personas',
        );
        return response.data.items;
    },

    /**
     * Start (or fetch) a conversation with an AI chatbot persona.
     * Returns the conversationId the UI should navigate to.
     */
    async startConversation(personaSlug: string): Promise<{
        conversationId: string;
        botUserId: string;
        isNew: boolean;
    }> {
        const response = await apiClient.post<{
            conversationId: string;
            botUserId: string;
            isNew: boolean;
        }>('/messaging/ai/start', { personaSlug });
        return response.data;
    },

    /**
     * Push-to-talk voice message: upload a WAV blob and let the server
     * transcribe → maybe-charge-quota → fire the LLM pipeline.
     * Bot reply arrives via the existing 'message' WebSocket event.
     */
    async sendVoice(args: { conversationId: string; audio: Blob }): Promise<SendVoiceResponse> {
        const fd = new FormData();
        fd.append('conversationId', args.conversationId);
        fd.append('audio', args.audio, 'utterance.wav');
        // CRITICAL: the shared apiClient has `Content-Type: application/json`
        // as a default header (set in createApiClient). If we don't override
        // it here, axios sends the FormData body BUT advertises it as JSON.
        // The server's JSON parser then chokes on the multipart boundary
        // bytes and throws a SyntaxError → 400 Bad Request, with no useful
        // log line because the failure happens before our controller runs.
        //
        // Setting Content-Type to undefined makes axios fall back to
        // auto-detection, which for FormData produces the correct
        // `multipart/form-data; boundary=----…` header.
        //
        // 25s timeout covers the longest realistic end-to-end (slow cellular
        // upload + ASR fallback). The backend's nudge TTS is fire-and-forget,
        // so this only has to span ASR + quota + sendMessage.
        const response = await apiClient.post<SendVoiceResponse>(
            '/messaging/ai/voice',
            fd,
            {
                timeout: 25_000,
                headers: { 'Content-Type': undefined as unknown as string },
            },
        );
        return response.data;
    },

    /**
     * Daily voice quota status — used / remaining / coin balance.
     */
    async getVoiceQuota(): Promise<VoiceQuotaStatus> {
        const response = await apiClient.get<VoiceQuotaStatus>('/messaging/ai/voice/quota');
        return response.data;
    },

    /**
     * Re-synthesise the bot voice for a message whose original TTS attempt
     * failed (audioUrl is null but content is present). The server enforces
     * a per-message 3-attempt cap.
     *
     * On success the new audio also arrives via the 'message_updated'
     * WebSocket event, so the bubble is updated regardless of which path
     * lands first.
     */
    async regenerateBotVoice(messageId: string): Promise<{
        ok: boolean;
        audioUrl?: string | null;
        audioDurationMs?: number | null;
        reason?: string;
    }> {
        const response = await apiClient.post<{
            ok: boolean;
            audioUrl?: string | null;
            audioDurationMs?: number | null;
            reason?: string;
        }>('/messaging/ai/voice/regenerate', { messageId });
        return response.data;
    },
};
